import React from 'react';
import '../../../styles/v4-theme.css';

/**
 * ActivitiesTab Component
 * Displays all activities/actions of a target
 */
const ActivitiesTab = ({ target, onTimestampClick }) => {
  const activities = target.actions?.activities || [];

  if (activities.length === 0) {
    return (
      <div className="v4-empty-state" style={{ padding: 'var(--space-16)' }}>
        <div className="v4-empty-state-icon">▶</div>
        <div className="v4-empty-state-title">No Activities Detected</div>
        <div className="v4-empty-state-description">
          No activities or actions were recorded for this target.
        </div>
      </div>
    );
  }

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'var(--confidence-high)';
    if (confidence >= 0.7) return 'var(--confidence-medium)';
    return 'var(--confidence-low)';
  };

  return (
    <div className="v4-activities-content" style={{ padding: 'var(--space-3)' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-3)',
        padding: 'var(--space-2)',
        background: 'rgba(59, 130, 246, 0.08)',
        borderRadius: '6px',
        border: '1px solid rgba(59, 130, 246, 0.2)'
      }}>
        <h3 style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--color-vehicle)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontFamily: 'Courier New, monospace',
          margin: 0
        }}>
          ▶ ACTIVITIES ({activities.length})
        </h3>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {activities.map((activity, idx) => (
          <div
            key={activity.activity_id || idx}
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              borderRadius: '8px',
              padding: 'var(--space-3)',
              transition: 'all var(--transition-base)'
            }}
          >
            {/* Activity Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-2)',
              paddingBottom: 'var(--space-2)',
              borderBottom: '1px dashed rgba(148, 163, 184, 0.2)'
            }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--accent)',
                  background: 'rgba(124, 58, 237, 0.2)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontFamily: 'Courier New, monospace'
                }}>
                  #{idx + 1}
                </span>
                <h4 style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--text-primary)',
                  margin: 0
                }}>
                  {activity.type || activity.activity || 'Unknown Activity'}
                </h4>
              </div>
              {activity.confidence !== undefined && (
                <div style={{
                  padding: '3px 8px',
                  background: `${getConfidenceColor(activity.confidence)}22`,
                  color: getConfidenceColor(activity.confidence),
                  borderRadius: '4px',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-bold)',
                  whiteSpace: 'nowrap',
                  border: `1px solid ${getConfidenceColor(activity.confidence)}44`
                }}>
                  {Math.round(activity.confidence * 100)}%
                </div>
              )}
            </div>

            {/* Activity Details */}
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {(activity.timestamp || activity.duration) && (
                <div style={{ 
                  display: 'flex', 
                  gap: 'var(--space-3)',
                  flexWrap: 'wrap'
                }}>
                  {activity.timestamp && (
                    <div style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      background: 'rgba(59, 130, 246, 0.08)',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', fontWeight: 'var(--font-semibold)' }}>
                        ▸
                      </span>
                      {onTimestampClick ? (
                        <span 
                          className="v4-timestamp-link"
                          onClick={() => onTimestampClick(activity.timestamp, target)}
                          title="Click to jump to video"
                          style={{
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-bold)',
                            fontFamily: 'Courier New, monospace'
                          }}
                        >
                          {activity.timestamp}
                        </span>
                      ) : (
                        <span style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--color-vehicle)',
                          fontWeight: 'var(--font-bold)',
                          fontFamily: 'Courier New, monospace'
                        }}>
                          {activity.timestamp}
                        </span>
                      )}
                    </div>
                  )}

                  {activity.duration && (
                    <div style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      border: '1px solid rgba(148, 163, 184, 0.2)'
                    }}>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                        ◷
                      </span>
                      <span style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-primary)',
                        fontWeight: 'var(--font-semibold)',
                        fontFamily: 'Courier New, monospace'
                      }}>
                        {activity.duration}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {activity.description && (
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-primary)',
                  lineHeight: '1.5',
                  background: 'rgba(0, 0, 0, 0.2)',
                  padding: 'var(--space-2)',
                  borderRadius: '4px',
                  borderLeft: '2px solid var(--accent)'
                }}>
                  {activity.description}
                </div>
              )}

              {activity.evidence_basis && (
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5',
                  background: 'rgba(0, 0, 0, 0.2)',
                  padding: 'var(--space-2)',
                  borderRadius: '4px',
                  fontStyle: 'italic',
                  borderLeft: '2px solid rgba(148, 163, 184, 0.3)'
                }}>
                  <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--text-muted)' }}>Evidence:</span> {activity.evidence_basis}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivitiesTab;

